import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CoachMark.css';

interface CoachMarkProps {
  id: string;
  message: string;
  buttonText?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  showAlways?: boolean;
  onDismiss?: () => void;
  children: React.ReactNode;
  wrapperClassName?: string;
}

export const CoachMark: React.FC<CoachMarkProps> = ({
  id,
  message,
  buttonText = 'Got it',
  position = 'top',
  showAlways = false,
  onDismiss,
  children,
  wrapperClassName = '',
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasSeen = localStorage.getItem(`coachmark_${id}`);
    if (!hasSeen || showAlways) {
      const timer = setTimeout(() => {
        if (targetRef.current) {
          const rect = targetRef.current.getBoundingClientRect();
          // ONLY show if the element is actually rendered and visible (not display: none)
          if (rect.width > 0 && rect.height > 0) {
            setCoords({
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              height: rect.height,
            });
            setVisible(true);
          }
        }
      }, 1000); // Slightly longer delay to let layouts settle
      return () => clearTimeout(timer);
    }
  }, [id, showAlways]);

  // Re-calculate on resize/scroll to keep it "stuck" if needed,
  // but for a one-off mark, the initial mount is usually sufficient.
  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      if (targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        setCoords({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [visible]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
    if (!showAlways) {
      localStorage.setItem(`coachmark_${id}`, 'true');
    }
    onDismiss?.();
  };

  const renderBubble = () => {
    if (!visible || !coords) return null;

    // Calculate bubble position based on target coords
    let bubbleStyle: React.CSSProperties = {};
    const GAP = 16;
    let actualPosition = position;
    const targetCenterX = coords.left + coords.width / 2;

    // Mobile fallback: if screen is narrow or if sides would overflow, flip to top/bottom
    if (
      window.innerWidth < 480 ||
      (position === 'left' && coords.left < 220) ||
      (position === 'right' && coords.left + coords.width > window.innerWidth - 220)
    ) {
      actualPosition = coords.top > 200 ? 'top' : 'bottom';
    }

    // Horizontal centering with safety margins
    const bubbleWidth = 240; // New premium width
    let leftPos = targetCenterX;

    if (actualPosition === 'top' || actualPosition === 'bottom') {
      leftPos = Math.max(
        bubbleWidth / 2 + 12,
        Math.min(window.innerWidth - bubbleWidth / 2 - 12, targetCenterX),
      );
      bubbleStyle = {
        left: leftPos,
        top: actualPosition === 'top' ? coords.top - GAP : coords.top + coords.height + GAP,
        transform: actualPosition === 'top' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
      };
    } else if (actualPosition === 'left') {
      bubbleStyle = {
        top: coords.top + coords.height / 2,
        left: coords.left - GAP,
        transform: 'translate(-100%, -50%)',
      };
    } else if (actualPosition === 'right') {
      bubbleStyle = {
        top: coords.top + coords.height / 2,
        left: coords.left + coords.width + GAP,
        transform: 'translateY(-50%)',
      };
    }

    // Calculate Arrow Offset to point exactly at target center
    // Arrow is relative to the bubble which is 50% translated if top/bottom
    const arrowStyle: React.CSSProperties = {};
    if (actualPosition === 'top' || actualPosition === 'bottom') {
      const offset = targetCenterX - leftPos;
      arrowStyle.left = `calc(50% + ${offset}px)`;
    }

    return createPortal(
      <div className="coachmark-portal-overlay">
        <div className={`coachmark-bubble coachmark-${actualPosition}`} style={bubbleStyle}>
          <div className="coachmark-content">
            <p className="coachmark-message">{message}</p>
            <button type="button" className="coachmark-btn" onClick={handleDismiss}>
              {buttonText}
            </button>
          </div>
          <div className="coachmark-arrow" style={arrowStyle} />
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className={`coachmark-container ${wrapperClassName}`} ref={targetRef}>
      {children}
      {visible && <div className="coachmark-pulse" />}
      {renderBubble()}
    </div>
  );
};
